import qs from 'query-string'
import isString from 'lodash.isstring'

import { SegmentTrack } from '@enums/segment'
import { AUTH_STATE } from '@enums/auth-state'

import {
  identify as segmentIdentify,
  trackApi as segmentTrackApi,
} from '@api/segment'
import { captureException as sentryCaptureException } from '@api/sentry'
import { ROUTE } from '@enums/route'
import { OktaSignFlow } from '@api/okta'
import {
  getAuthProvider,
  getOktaFlow,
  ThriveAuthProviders,
  UserData,
} from '@util/thrive-user-data'

import { listenHubEvents } from './listen-hub-events'
import { onLoad } from './on-load'
import { AppErrorDispatchType } from 'contexts/AppError'

export type TAuthState = AUTH_STATE | null
export type SetAuthState = React.Dispatch<SetStateAction<TAuthState>>
type SetStateAction<S> = S | ((prevState: S) => S)

export { listenHubEvents, onLoad }

/**
 * redirect the user internally to the private route (aka logged in view)
 * via setting the auth state, or redirect the user to the procured `redirect_url`
 * query param — used by Thrive Academy for seamlessly redirecting the user back
 * to its page after successfully signing in the user
 */
export function redirectOnSuccess(setAuthState: SetAuthState): void {
  const parsed = qs.parse(window.location.search)
  const redirectUrl = isString(parsed?.redirect_url)
    ? decodeURIComponent(parsed.redirect_url)
    : ''

  if (redirectUrl) {
    window.location.replace(redirectUrl as string)
    return
  }

  setAuthState(AUTH_STATE.AUTHD)
}

export function handleSegment({ thriveUserId, companyId, cohortId }: UserData): void {
  /**
   * identify the Thrive user for future Segment tracking calls
   */
  segmentIdentify({
    user_id: thriveUserId,
    company_id: companyId,
    cohort_id: cohortId
  })

  const location = window.location.pathname

  /**
   * a garbage hack to deal with the fact that Cognito needs the SIGN_IN
   * event called after the SIGN_UP event to properly register a user on
   * signing them up. this means we can't accurately tell Segment whether a
   * user is signing in or signing up based on the Cognito Hub events.
   * without this check, a sign in event would always accompany a sign up
   * event in Segment, like it does for Amplify
   */
  if (
    location.includes(ROUTE.signUp) ||
    location.includes(ROUTE.signUpDetails) ||
    (getOktaFlow() === OktaSignFlow.signUp &&
      getAuthProvider() === ThriveAuthProviders.okta)
  ) {
    segmentTrackApi(SegmentTrack.signUp, true, {
      company_id: companyId,
    })
  } else if (
    location.includes(ROUTE.logIn) ||
    location.includes(ROUTE.password)
  ) {
    segmentTrackApi(SegmentTrack.signIn, true)
  }
}

/**
 * localStorage needs to be cleared of Cognito data and the Thrive user ID
 * on sign-out / forced exit (token expire)
 * Keep keys related to language
 */
export function clearLocalStorage(): void {
  const SELECTED_LANGUAGE_KEY = 'ThriveWebApp_SelectedLanguage'
  const TRANSLATIONS_KEY = 'ThriveWebApp_Translations'

  const selectedLanguage = localStorage.getItem(SELECTED_LANGUAGE_KEY) || ''
  const translations = localStorage.getItem(TRANSLATIONS_KEY) || ''

  localStorage.clear()
  
  localStorage.setItem(SELECTED_LANGUAGE_KEY, selectedLanguage)
  localStorage.setItem(TRANSLATIONS_KEY, translations)
}

export function onError(
  err: Error,
  setAuthState: SetAuthState,
  appErrorDispatch: AppErrorDispatchType
) {
  console.error(err)
  sentryCaptureException(err)
  clearLocalStorage()
  setAuthState(AUTH_STATE.UNAUTHD)
  appErrorDispatch(true)
}
